/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import Countdown, { CountdownRenderProps } from 'react-countdown';
import { ProfileState } from 'store/modules/profile_store/types';
import { PanelState } from 'store/modules/panel_store/types';
import { getPanelProperties } from 'store/modules/panel_store/actions';
import tabId from 'services/tabId';
import { ApplicationState } from '../../store';
import { Container } from './style';
import Loader from '../../components/atoms/loader';
import { getUsageMapping } from '../../services/actions/get_usage_mapping';
import {
  buildUser,
  RenewalUser,
  cycles,
  plans,
  planFeatures,
  heroBenefits,
  comparisonWithout,
  comparisonRenewing,
  legalChips,
  courseChips,
  formatBRL,
  RenewalCycle,
  RenewalPlanData,
} from './data';
import {
  CheckIcon,
  XIcon,
  ChatIcon,
  ShieldIcon,
  CardIcon,
  LockIcon,
  HeartIcon,
  ArrowRightIcon,
  LegalIllustration,
  CourseIllustration,
} from './icons';

const formatInt = (n: number) => n.toLocaleString('pt-BR');

interface UserProps {
  user: RenewalUser;
}

interface UserSectionProps extends UserProps {
  firstName: string;
}

const Hero = ({ user, firstName }: UserSectionProps) => (
  <section className="hero">
    <div className="container hero__inner">
      <span className="hero__eyebrow">10% OFF</span>
      <h1 className="hero__title">
        Olá, {firstName}. <br />
        Faça a renovação <span className="hero__title-em">antecipada</span> do seu plano!
      </h1>
      <p className="hero__subtitle">
        Você acompanhou <strong>{formatInt(user.stats.opportunities)} oportunidades</strong> nos
        últimos {user.cycleMonths} meses. Não perca o ritmo — e ainda economize 10% renovando antes
        do vencimento.
      </p>
      <ul className="hero__benefits">
        {heroBenefits.map((benefit) => (
          <li key={benefit}>
            <span className="check-circle">
              <CheckIcon />
            </span>
            <span>{benefit}</span>
          </li>
        ))}
      </ul>
      <div className="hero__cta-row">
        <a href="#planos" className="btn btn--primary btn--lg">
          Renovar com 10% OFF
        </a>
        <a href="#consultor" className="btn btn--outline btn--lg">
          Falar com um consultor
        </a>
      </div>
    </div>
  </section>
);

const SIMULATOR_FULL_PRICE = 3190.0;
const SIMULATOR_DISCOUNTED = 2871.0;

const Simulator = () => (
  <section className="simulator section">
    <div className="container simulator__inner">
      <div className="simulator__copy">
        <span className="eyebrow">Veja na prática</span>
        <h2 className="simulator__title">Quanto você economiza renovando antes do vencimento</h2>
        <p className="simulator__subtitle">
          Renovar antecipadamente garante <strong>10% de desconto imediato</strong> em qualquer um
          dos planos.
        </p>
        <ul className="simulator__bullets">
          <li>
            <span className="check-circle">
              <CheckIcon />
            </span>
            Desconto aplicado automaticamente no checkout
          </li>
          <li>
            <span className="check-circle">
              <CheckIcon />
            </span>
            Em até 5x sem juros no cartão
          </li>
          <li>
            <span className="check-circle">
              <CheckIcon />
            </span>
            Preço congelado por mais um ciclo completo
          </li>
        </ul>
        <a href="#planos" className="btn btn--primary btn--lg simulator__cta">
          Renovar agora
        </a>
      </div>
      <div className="simulator__card">
        <div className="simulator__card-head">
          <span className="simulator__card-eyebrow">Simulador de renovação</span>
          <span className="simulator__card-pill">10% OFF aplicado</span>
        </div>
        <h3 className="simulator__card-plan">Plano Premium · Anual</h3>
        <div className="simulator__split">
          <div className="simulator__col">
            <span className="simulator__col-label">De</span>
            <span className="simulator__col-price simulator__col-price--strike">
              {formatBRL(SIMULATOR_FULL_PRICE)}
            </span>
            <span className="simulator__col-pay">Pagamento à vista</span>
          </div>
          <div className="simulator__arrow" aria-hidden="true">
            <ArrowRightIcon size={22} />
          </div>
          <div className="simulator__col simulator__col--accent">
            <span className="simulator__col-label">Por</span>
            <span className="simulator__col-price">{formatBRL(SIMULATOR_DISCOUNTED)}</span>
            <span className="simulator__col-pay">Em até 5x sem juros</span>
          </div>
        </div>
        <div className="simulator__economy">
          <span className="simulator__economy-label">Você economiza</span>
          <strong className="simulator__economy-value">
            {formatBRL(SIMULATOR_FULL_PRICE - SIMULATOR_DISCOUNTED)}
          </strong>
        </div>
      </div>
    </div>
  </section>
);

interface KpiCardProps {
  label: string;
  value: string;
  delta?: string;
  positive?: boolean;
}

const KpiCard = ({ label, value, delta, positive }: KpiCardProps) => (
  <div className="kpi-card">
    <span className="kpi-card__label">{label}</span>
    <span className="kpi-card__value">{value}</span>
    <span className={`kpi-card__delta ${positive ? 'is-positive' : ''}`}>{delta}</span>
  </div>
);

const Stats = ({ user, firstName }: UserSectionProps) => {
  const deliveryKpis = [
    { label: 'Licitações', value: user.stats.opportunities },
    { label: 'Acompanhamentos', value: user.stats.followups },
    { label: 'Boletins', value: user.stats.bulletins },
  ].filter((kpi) => kpi.value >= 1);

  const panels = [
    { title: 'Licitações', values: user.monthly.map((month) => month.licitacoes) },
    { title: 'Acompanhamentos', values: user.monthly.map((month) => month.acompanhamentos) },
    { title: 'Boletins', values: user.monthly.map((month) => month.boletins) },
  ];

  return (
    <section className="stats section">
      <div className="container stats__head">
        <span className="eyebrow">Seu impacto com o ConLicitação</span>
        <h2 className="section-title">
          {firstName}, veja o que você construiu nos últimos {user.cycleMonths} meses
        </h2>
      </div>

      {deliveryKpis.length > 0 && (
        <div className="container stats__group">
          <h3 className="stats__group-title">Oportunidades entregues</h3>
          <div className="stats__grid">
            {deliveryKpis.map((kpi) => (
              <KpiCard key={kpi.label} label={kpi.label} value={formatInt(kpi.value)} />
            ))}
          </div>
        </div>
      )}

      {user.monthly.length > 0 && (
        <div className="container">
          <div className="stats__chart-card">
            <div className="stats__chart-head">
              <h3>Atividade ao longo do ciclo</h3>
            </div>
            <div className="stats__multiples">
              {panels.map((panel) => {
                const peak = Math.max(...panel.values, 1);
                return (
                  <div className="stats__panel" key={panel.title}>
                    <div className="stats__panel-head">
                      <span className="stats__panel-title">{panel.title}</span>
                    </div>
                    <div className="stats__panel-chart">
                      {user.monthly.map((month, idx) => (
                        <div className="stats__bar-group" key={month.label}>
                          <div className="stats__bars">
                            <span
                              className="stats__bar stats__bar--primary"
                              style={{ height: `${Math.round((panel.values[idx] / peak) * 100)}%` }}
                              title={`${formatInt(panel.values[idx])} ${panel.title.toLowerCase()}`}
                            />
                          </div>
                          <span className="stats__bar-label">{month.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

interface PlanCardProps {
  plan: RenewalPlanData;
  cycle: RenewalCycle;
}

const PlanCard = ({ plan, cycle }: PlanCardProps) => {
  const pricing = plan.pricing[cycle.id];

  return (
    <article className="plan-card">
      <header className="plan-card__head">
        <h3>{plan.name}</h3>
        <span className="plan-card__cycle-badge">{cycle.label}</span>
      </header>
      <div className="plan-card__price">
        <span className="plan-card__amount">{formatBRL(pricing.full)}</span>
        <span className="plan-card__pay">no boleto à vista ou</span>
        <div className="plan-card__discounted">
          <span className="plan-card__amount-discounted">{formatBRL(pricing.discounted)}</span>
          <span className="plan-card__off">(10% Off)</span>
        </div>
        <span className="plan-card__pay">até {cycle.installments}x sem juros no cartão</span>
      </div>
      <a href="#consultor" className="btn btn--primary btn--block plan-card__cta">
        QUERO RENOVAR
      </a>
      <ul className="plan-card__features">
        {planFeatures.map((feature) => {
          const available = feature.availability ? feature.availability[plan.id] : true;
          return (
            <li key={feature.name} className={available ? '' : 'is-unavailable'}>
              {available ? (
                <span className="plan-card__check">
                  <CheckIcon size={11} />
                </span>
              ) : (
                <span className="plan-card__x">
                  <XIcon size={11} />
                </span>
              )}
              <span className="plan-card__feature-name">{feature.name}</span>
              {feature.ia && <span className="plan-card__ia">IA</span>}
            </li>
          );
        })}
      </ul>
    </article>
  );
};

const Plans = () => {
  const [cycleId, setCycleId] = useState<string>('anual');
  const cycle = cycles.find((c) => c.id === cycleId) as RenewalCycle;

  return (
    <section className="plans" id="planos">
      <div className="container plans__head">
        <span className="eyebrow">Investimento</span>
        <h2 className="section-title">Renove e continue à frente do mercado</h2>
        <p className="section-subtitle">
          Mantenha tudo rodando e ainda economize 10% renovando agora.
        </p>
        <div className="plans__cycle" role="tablist">
          {cycles.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={cycleId === c.id}
              className={`plans__cycle-btn ${cycleId === c.id ? 'is-active' : ''}`}
              onClick={() => setCycleId(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div className="container plans__grid">
        {plans.map((plan) => (
          <PlanCard key={plan.id} plan={plan} cycle={cycle} />
        ))}
      </div>
    </section>
  );
};

const Comparison = () => (
  <section className="comparison section">
    <div className="container comparison__head">
      <span className="eyebrow">Antes de decidir</span>
      <h2 className="section-title">O que muda no dia seguinte ao vencimento</h2>
      <p className="section-subtitle">
        Renovar a tempo não é só economizar 10%. É manter intacta a operação que você levou meses
        para construir.
      </p>
    </div>
    <div className="container comparison__grid">
      <div className="comparison__col comparison__col--lose">
        <header>
          <span className="comparison__pill comparison__pill--lose">Sem renovar</span>
          <h3>Você perde acesso e ritmo</h3>
        </header>
        <ul>
          {comparisonWithout.map((item) => (
            <li key={item}>
              <span className="comparison__icon comparison__icon--lose">
                <XIcon size={12} />
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="comparison__col comparison__col--gain">
        <header>
          <span className="comparison__pill comparison__pill--gain">Renovando agora</span>
          <h3>Tudo segue rodando — e mais barato</h3>
        </header>
        <ul>
          {comparisonRenewing.map((item) => (
            <li key={item}>
              <span className="comparison__icon comparison__icon--gain">
                <CheckIcon size={12} />
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const Extras = () => (
  <section className="extras section">
    <div className="container extras__head">
      <span className="eyebrow">Incluído na sua renovação</span>
      <h2 className="section-title">Mais do que uma plataforma — um time inteiro do seu lado</h2>
    </div>
    <div className="container extras__blocks">
      <div className="extras__block">
        <div className="extras__art" aria-hidden="true">
          <LegalIllustration />
        </div>
        <div className="extras__content">
          <h3>28 anos de expertise jurídica ao seu alcance</h3>
          <p>
            Equipe de especialistas com mais de 28 anos de experiência no mercado de vendas para o
            governo, disponível para apoiar em todas as etapas do processo licitatório.
          </p>
          <div className="extras__chips">
            {legalChips.map((c) => (
              <span key={c} className="chip">
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="extras__block">
        <div className="extras__art" aria-hidden="true">
          <CourseIllustration />
        </div>
        <div className="extras__content">
          <h3>+150 cursos para vender mais para o governo</h3>
          <p>
            Acesso a todo este conteúdo prático, com mais de 150 cursos especializados, aulas
            práticas e novos conteúdos atualizados mensalmente.
          </p>
          <div className="extras__chips">
            {courseChips.map((c) => (
              <span key={c} className="chip">
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const FinalCta = ({ user }: UserProps) => (
  <section className="final-cta section" id="consultor">
    <div className="container">
      <div className="final-cta__card">
        <Countdown
          date={user.offerEndsAt}
          zeroPadTime={2}
          zeroPadDays={2}
          renderer={({ days, formatted }: CountdownRenderProps) => (
            <>
              <span className="final-cta__seal">
                <span className="final-cta__seal-dot" />
                JANELA DE 10% OFF · ACABA EM {days} DIAS
              </span>
              <h2 className="final-cta__title">
                Sua próxima licitação não espera. <br />
                Renove hoje, economize 10%.
              </h2>
              <p className="final-cta__subtitle">
                Mantenha sua operação rodando sem interrupção e garanta o melhor preço do ano para o
                seu plano.
              </p>
              <div className="final-cta__timer">
                <div className="final-cta__unit">
                  <span className="final-cta__unit-value">{formatted.days}</span>
                  <span className="final-cta__unit-label">dias</span>
                </div>
                <div className="final-cta__unit">
                  <span className="final-cta__unit-value">{formatted.hours}</span>
                  <span className="final-cta__unit-label">horas</span>
                </div>
                <div className="final-cta__unit">
                  <span className="final-cta__unit-value">{formatted.minutes}</span>
                  <span className="final-cta__unit-label">min</span>
                </div>
                <div className="final-cta__unit">
                  <span className="final-cta__unit-value">{formatted.seconds}</span>
                  <span className="final-cta__unit-label">seg</span>
                </div>
              </div>
            </>
          )}
        />
        <div className="final-cta__actions">
          <a href="#planos" className="btn btn--primary btn--lg">
            Renovar com 10% off
          </a>
          <a href="#consultor" className="btn btn--outline btn--lg final-cta__outline">
            Falar com um consultor
          </a>
        </div>
        <div className="final-cta__trust">
          <span>
            <LockIcon /> Pagamento 100% seguro
          </span>
          <span>
            <CardIcon /> Em até 5x sem juros
          </span>
          <span>
            <ShieldIcon /> Garantia de preço congelado
          </span>
          <span>
            <HeartIcon /> Suporte humano em todas as etapas
          </span>
        </div>
      </div>
    </div>
  </section>
);

const PageFooter = () => (
  <footer className="site-footer">
    <div className="container site-footer__inner">
      <p>
        Para mais informações ou dúvidas sobre as condições de renovação,{' '}
        <a href="#consultor">fale com um dos nossos consultores</a>.
      </p>
      <span className="site-footer__brand">© ConLicitação · Todos os direitos reservados</span>
    </div>
  </footer>
);

const FloatingCta = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 480);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <a
      href="#consultor"
      className={`floating-cta ${visible ? 'is-visible' : ''}`}
      aria-label="Falar com um consultor"
    >
      <ChatIcon size={18} />
      <span>Fale com um consultor</span>
    </a>
  );
};

const RenewalEmail = () => {
  const [userData, setUserData] = useState<RenewalUser | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const { panel, request } = useSelector<ApplicationState, PanelState>(
    (state) => state.session.panel,
  );
  const dispatch = useDispatch();
  const firstName = (profile?.name ?? '').split(' ')[0];

  useEffect(() => {
    document.title = 'Renove sua assinatura · ConLicitação';
    dispatch(
      getPanelProperties(profile?.representant ? profile?.currentCompany[tabId()] : undefined),
    );

    const fetchData = async () => {
      try {
        const data = await getUsageMapping();
        if (data?.venda) setUserData(buildUser(data));
      } catch (error: any) {
        if (error?.response?.status === 403) setForbidden(true);
        setUserData(null);
      }
    };

    fetchData();
  }, []);

  if (
    forbidden ||
    (request?.loaded && !request?.loading && !panel?.renewal_campaign?.released_online)
  ) {
    return <Redirect to="/painel" />;
  }

  if (!userData) {
    return (
      <div
        className="d-flex mx-auto justify-content-center align-items-center"
        style={{ height: '70vh' }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <Container>
      <main>
        <Hero user={userData} firstName={firstName || userData.firstName} />
        <Simulator />
        <Stats user={userData} firstName={firstName || userData.firstName} />
        <Plans />
        <Comparison />
        <Extras />
        <FinalCta user={userData} />
      </main>
      <PageFooter />
      <FloatingCta />
    </Container>
  );
};

export default RenewalEmail;
